/**
 * Report issue 에 함께 보내는 Studio 로그 버퍼.
 *
 * Studio 이슈는 로그를 잡을 수 있다. 원본 파일은 서버에 보관하지 않으므로
 * 리포트를 판단할 근거는 사용자가 고른 증상과 이 로그뿐이다.
 *
 * React 쪽 console 과 Unity iframe 의 console 을 하나의 버퍼에 모은다. 증상
 * 여섯 개 중 Export·저장·로드 실패는 Unity 쪽에서 나기 때문에 React console 만
 * 모으면 정작 필요한 줄이 빠진다.
 *
 * 프로덕션 빌드는 `silenceConsoleInProduction()` 이 console 을 noop 으로 바꾼다.
 * 그래서 이 버퍼는 그 뒤에 설치해 noop 을 감싼다. 화면에 찍히지 않아도 기록은
 * 남는다.
 *
 * token·이메일·signed URL 은 버퍼에 넣기 전에 지운다. 서버에서 지우면 마스킹
 * 전 원문이 한 번은 네트워크를 탄다.
 */

/** 최근 몇 줄까지 들고 있을지. 오래된 줄부터 버린다. */
export const LOG_BUFFER_MAX_LINES = 300;

/** 버퍼 전체 상한. 초과하면 오래된 줄부터 버린다. */
export const LOG_BUFFER_MAX_BYTES = 64 * 1024;

/** 한 줄이 통째로 버퍼를 먹지 않도록 자른다. */
const MAX_LINE_LENGTH = 2000;

/** Unity iframe 은 Space 전환마다 다시 뜬다. 새 window 마다 다시 붙는다. */
const UNITY_IFRAME_ID = "unity-iframe";
const UNITY_ATTACH_INTERVAL_MS = 3000;

type CapturedLevel = "log" | "info" | "warn" | "error";

const CAPTURED_LEVELS: CapturedLevel[] = ["log", "info", "warn", "error"];

interface BufferedLine {
  text: string;
  bytes: number;
}

let lines: BufferedLine[] = [];
let bufferedBytes = 0;
let installed = false;
let unityAttachTimer: ReturnType<typeof setInterval> | null = null;
const attachedWindows = new WeakSet<Window>();

const MASK = "[redacted]";

/**
 * 지우는 대상은 넷이다 — Bearer token, token 계열 query/JSON 값, 이메일,
 * 서명이 붙은 URL. 서명 URL 은 query 를 통째로 버린다. 경로만 남아도 어떤
 * 리소스였는지는 알 수 있다.
 */
interface Redaction {
  pattern: RegExp;
  replacement: string;
}

const REDACTIONS: Redaction[] = [
  { pattern: /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, replacement: `Bearer ${MASK}` },
  {
    pattern:
      /\b(id_?token|access_?token|refresh_?token|token|apikey|api_key)(["']?\s*[:=]\s*["']?)[A-Za-z0-9._~+/-]{8,}=*/gi,
    replacement: `$1$2${MASK}`,
  },
  {
    pattern: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g,
    replacement: MASK,
  },
  {
    pattern:
      /(https?:\/\/[^\s"']+)\?[^\s"']*(?:X-Amz-Signature|Signature=|GoogleAccessId|Expires=)[^\s"']*/gi,
    replacement: `$1?${MASK}`,
  },
];

export const maskSensitive = (value: string): string =>
  REDACTIONS.reduce<string>(
    (text, { pattern, replacement }) => text.replace(pattern, replacement),
    value,
  );

const stringifyArgument = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "[unserializable]";
    }
  }
  return String(value);
};

const trimToBudget = (): void => {
  while (
    lines.length > LOG_BUFFER_MAX_LINES ||
    bufferedBytes > LOG_BUFFER_MAX_BYTES
  ) {
    const dropped = lines.shift();
    if (!dropped) break;
    bufferedBytes -= dropped.bytes;
  }
};

/** 한 줄을 버퍼에 넣는다. 이 함수만이 버퍼에 쓰는 유일한 경로다. */
export const recordReportLogLine = (
  source: "studio" | "unity",
  level: CapturedLevel,
  message: string,
): void => {
  const masked = maskSensitive(message).slice(0, MAX_LINE_LENGTH);
  if (!masked.trim()) return;
  const text = `[${new Date().toISOString()}] [${source}/${level}] ${masked}`;
  const entry: BufferedLine = { text, bytes: text.length };
  lines.push(entry);
  bufferedBytes += entry.bytes;
  trimToBudget();
};

const patchConsole = (target: Window, source: "studio" | "unity"): void => {
  const targetConsole = (target as Window & { console?: Console }).console;
  const consoleRef = targetConsole as unknown as Record<string, unknown>;
  if (!consoleRef) return;

  CAPTURED_LEVELS.forEach((level) => {
    const original = consoleRef[level];
    if (typeof original !== "function") return;
    const originalFn = original as (...args: unknown[]) => void;
    consoleRef[level] = (...args: unknown[]) => {
      try {
        recordReportLogLine(source, level, args.map(stringifyArgument).join(" "));
      } catch {
        // 기록이 실패해도 원래 console 동작은 그대로 둔다.
      }
      originalFn.apply(targetConsole, args);
    };
  });
};

const attachUnityConsole = (): void => {
  if (typeof document === "undefined") return;
  const iframe = document.getElementById(
    UNITY_IFRAME_ID,
  ) as HTMLIFrameElement | null;
  if (!iframe) return;
  let frameWindow: Window | null = null;
  try {
    // Unity 는 같은 origin 의 /unity 아래에서 뜨지만, 접근이 막히는 순간이
    // 있으므로 실패를 정상 경로로 취급한다.
    frameWindow = iframe.contentWindow;
  } catch {
    return;
  }
  if (!frameWindow || attachedWindows.has(frameWindow)) return;
  try {
    patchConsole(frameWindow, "unity");
    attachedWindows.add(frameWindow);
  } catch {
    // cross-origin 이거나 아직 document 가 없으면 다음 주기에 다시 시도한다.
  }
};

/**
 * 버퍼를 설치한다. `silenceConsoleInProduction()` 뒤에 부르는 것이 전제다.
 */
export const installReportLogCapture = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  patchConsole(window, "studio");

  window.addEventListener("error", (event) => {
    const detail =
      event.error instanceof Error
        ? `${event.error.name}: ${event.error.message}`
        : event.message;
    recordReportLogLine("studio", "error", `uncaught ${detail}`);
  });

  window.addEventListener("unhandledrejection", (event) => {
    recordReportLogLine(
      "studio",
      "error",
      `unhandled rejection ${stringifyArgument(event.reason)}`,
    );
  });

  attachUnityConsole();
  unityAttachTimer = setInterval(attachUnityConsole, UNITY_ATTACH_INTERVAL_MS);
};

/** 리포트에 실어 보낼 로그. 오래된 줄이 위, 최근 줄이 아래다. */
export const collectReportLog = (): string | null => {
  attachUnityConsole();
  if (lines.length === 0) return null;
  return lines.map((line) => line.text).join("\n");
};

/** 테스트 전용. 런타임 경로에서는 버퍼를 비우지 않는다. */
export const resetReportLogBufferForTest = (): void => {
  lines = [];
  bufferedBytes = 0;
  installed = false;
  if (unityAttachTimer !== null) {
    clearInterval(unityAttachTimer);
    unityAttachTimer = null;
  }
};
